import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/api/client';
import { useCreateTeam, useDeleteTeam, useUpdateTeam } from '@/api/teams';
import type { Team, User } from '@/api/types';
import { Button, Checkbox, Input, Modal, Select, Textarea, useToast } from '@/components/ui';
import { humanize } from '@/lib/format';
import { STRATEGY_HELP } from './helpers';

const schema = z.object({
  name: z.string().trim().min(2, 'At least 2 characters').max(80),
  description: z.string().max(500),
  assignmentStrategy: z.enum(['round_robin', 'least_loaded', 'skills_based', 'manual']),
  autoAssign: z.boolean(),
  autoReassignMinutes: z
    .string()
    .refine((v) => v === '' || (/^\d+$/.test(v) && +v >= 1 && +v <= 1440), '1–1440 minutes, or blank'),
  supervisorId: z.string(),
  isActive: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function TeamFormModal({
  team,
  users,
  onClose,
}: {
  team: Team | null;
  users: User[];
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateTeam();
  const update = useUpdateTeam();
  const remove = useDeleteTeam();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const supervisors = users.filter((u) => u.role !== 'agent');

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: team?.name ?? '',
      description: team?.description ?? '',
      assignmentStrategy: team?.assignmentStrategy ?? 'least_loaded',
      autoAssign: team?.autoAssign ?? true,
      autoReassignMinutes: team?.autoReassignMinutes ? String(team.autoReassignMinutes) : '',
      supervisorId: team?.supervisorId ?? '',
      isActive: team?.isActive ?? true,
    },
  });

  const onError = (e: Error) => {
    if (e instanceof ApiError && e.status === 409) setError('name', { message: e.message });
    else toast.error(e.message);
  };

  const onSubmit = handleSubmit((v) => {
    const input = {
      name: v.name,
      description: v.description,
      assignmentStrategy: v.assignmentStrategy,
      autoAssign: v.autoAssign,
      autoReassignMinutes: v.autoReassignMinutes ? Number(v.autoReassignMinutes) : null,
      supervisorId: v.supervisorId || null,
      isActive: v.isActive,
    };
    if (team)
      update.mutate(
        { id: team.id, input },
        { onSuccess: () => (toast.success('Team saved'), onClose()), onError },
      );
    else create.mutate(input, { onSuccess: () => (toast.success('Team created'), onClose()), onError });
  });

  const strategy = watch('assignmentStrategy');

  return (
    <Modal
      open
      onClose={onClose}
      title={team ? `Edit ${team.name}` : 'New team'}
      footer={
        <>
          {team && (
            <Button
              variant={confirmDelete ? 'danger' : 'ghost'}
              className="mr-auto"
              loading={remove.isPending}
              onClick={() =>
                confirmDelete
                  ? remove.mutate(team.id, {
                      onSuccess: () => (toast.success('Team deleted'), onClose()),
                      onError: (e) => toast.error(e.message),
                    })
                  : setConfirmDelete(true)
              }
            >
              {confirmDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="team-form" loading={create.isPending || update.isPending}>
            {team ? 'Save' : 'Create team'}
          </Button>
        </>
      }
    >
      <form id="team-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <Input
          label="Name"
          required
          placeholder="Payments & Billing"
          error={errors.name?.message}
          {...register('name')}
        />
        <Textarea
          label="Description"
          rows={2}
          error={errors.description?.message}
          {...register('description')}
        />
        <Select
          label="Assignment strategy"
          hint={STRATEGY_HELP[strategy]}
          {...register('assignmentStrategy')}
        >
          {(['least_loaded', 'skills_based', 'round_robin', 'manual'] as const).map((s) => (
            <option key={s} value={s}>
              {humanize(s)}
            </option>
          ))}
        </Select>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Auto-reassign after (minutes)"
            inputMode="numeric"
            placeholder="Off"
            hint="If the assignee hasn’t replied in time"
            error={errors.autoReassignMinutes?.message}
            {...register('autoReassignMinutes')}
          />
          <Select label="Supervisor" hint="Gets SLA escalations" {...register('supervisorId')}>
            <option value="">None</option>
            {supervisors.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({humanize(u.role)})
              </option>
            ))}
          </Select>
        </div>
        <Checkbox
          label="Auto-assign"
          description="Off = tickets wait in the team queue."
          {...register('autoAssign')}
        />
        <Checkbox label="Active" {...register('isActive')} />
      </form>
    </Modal>
  );
}
